import type { ChatClient } from '@wtfalch/chat';
import { Button, Icon, Panes } from '@wtfalch/design';

import type { ChatLocation } from '../routes.ts';
import { RoomComposer } from './RoomComposer.tsx';
import { Stream } from './Stream.tsx';
import { useTimeline } from './useTimeline.ts';

/**
 * A thread, in the third column -- matching the canvas's thread panel in
 * `chat()`. `client.timeline(roomId, threadId)` already answers "root
 * first," so the root message is simply the first row `Stream` draws; no
 * separate fetch or layout for it.
 *
 * Two buttons, one handler: at the phone width `chat.css` switches to, the
 * back arrow (the room's own `chat-back`) is the way back to the room and the
 * close button is hidden; at every other width it is the other way round.
 */
export function ThreadPanel({
  client,
  roomId,
  roomName,
  threadId,
  onClose,
  linkFor,
}: {
  client: ChatClient;
  roomId: string;
  /** The room the thread belongs to, for the head's subtitle. */
  roomName: string;
  threadId: string;
  onClose: () => void;
  /** The address of a place in a room, for a message's "Copy link". */
  linkFor?: (location: ChatLocation) => string;
}) {
  const timeline = useTimeline(client, roomId, threadId);
  // `client.timeline(roomId, threadId)` answers root first, so the replies
  // are everything after it.
  const replies = Math.max(0, timeline.page.events.length - 1);
  const count =
    replies === 0 ? 'No replies yet' : `${replies} ${replies === 1 ? 'reply' : 'replies'}`;

  return (
    <Panes.Column width={360} className="chat-third">
      <aside className="flex flex-1 flex-col min-h-0">
        <div className="flex items-center gap-3 chat-third-head">
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            className="chat-back"
            aria-label="Back to the room"
            onPress={onClose}
          >
            <Icon name="back" size={18} />
          </Button>
          <span className="flex flex-col grow min-w-0">
            <span className="text-sm font-strong">Thread</span>
            <span className="text-xs text-muted truncate">
              {count} · in {roomName}
            </span>
          </span>
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            className="chat-third-close"
            aria-label="Close the thread"
            onPress={onClose}
          >
            <Icon name="close" size={18} />
          </Button>
        </div>
        <Stream
          client={client}
          roomId={roomId}
          threadId={threadId}
          timeline={timeline}
          {...(linkFor ? { linkFor } : {})}
          emptyLabel="Loading this thread."
        />
        <RoomComposer
          key={threadId}
          client={client}
          roomId={roomId}
          threadId={threadId}
          placeholder="Reply in thread…"
        />
      </aside>
    </Panes.Column>
  );
}
